import { FormControl, RadioGroup, FormControlLabel, Radio, Divider, Box, Button, Typography, Alert } from '@mui/material'
import Grid from '@mui/material/Grid'
import capitalize from 'lodash/capitalize'
import PropTypes from 'prop-types'
import React, { useMemo, useEffect, useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import CreditCardAchListing from 'components/billing/groups/payment/take-payments/CreditCardAchListing'
import SurchargePanel from 'components/billing/payments/SurchargePanel'
import CommonSwitch from 'components/common/CommonSwitch'
import AutopayDisclaimer from 'components/common/payments/AutopayDisclaimer'
import usePaymentProvider from 'components/customer-details/content/payment-details/usePaymentProvider'
import FortisElements from 'components/customer-details/content/payment-details/FortisElements'
import { HHFormTextField, HHFormCheckboxField, HHFormSwitchField } from 'components/form-fields/v5'
import HHFormDesktopDatePicker from 'components/form-fields/v5/HHFormDesktopDatePicker'
import HHFormPriceField from 'components/form-fields/v5/HHFormPriceField'
import { useAccountingLockValue } from 'providers/AccountingLockProvider'
import { PAYMENT_METHODS, DEFAULT_AUTOPAY_TIMING } from 'settings/constants/payment'
import T from 'T'
import { formatDateToBEFormatDateFns } from 'utils/date'
import { get } from 'utils/lodash'
import { formatCentsToDollars } from 'utils/price'
import {
  getPaymentMethodByStatus,
  getIsCreditCardOrACH,
  getIsCash,
  getIsCreditCard,
  getSavedMethodsByPaymentType,
  getIsAccountCredit,
  checkHasZeroOrNegativeInvoices,
} from 'utils/takePayments'

import AddToGroupField from './AddToGroupField'
import AmountField from './AmountField'
import RenderACH from './RenderACH'
import RenderCreditCard from './RenderCreditCard'

const { CASH, CHECK, CREDIT_CARD, ACH, OTHER, ACCOUNT_CREDIT } = PAYMENT_METHODS

const PaymentType = ({
  isLoading = false,
  hasSurcharge = false,
  savedPaymentMethods = [],
  params = {},
  unpaidInvoices = [],
  initialCreditBalanceCents = 0,
  isSinglePayment = false,
  sortedUnpaidInvoices = [],
  onChange,
  onAmountChange,
  onNextBtnClick,
  onClearClick,
  onAccountCreditAmountChange,
  accountId,
  fortisSubmitRef,
  onFortisTokenized,
  onFortisLoadingChange,
  billingAddress = {},
}) => {
  const {
    control,
    watch,
    setValue,
    getValues,
    setFocus,
    formState: { errors },
    clearErrors,
  } = useFormContext()

  const { selectedOption, groupId } = params
  const watchAmount = watch('amount')
  const watchPaymentMethod = watch('paymentMethod')
  const watchSelectedPaymentTokenDetails = watch('selectedPaymentTokenDetails')
  const watchIsNewCard = watch('isNewCard')
  const watchIsSaveNewMethod = watch('isSaveNewMethod')
  const watchAutoPayTime = watch('autoPayTime')
  const { checkIsLocked } = useAccountingLockValue()
  const applyAccountCredit = watch('applyAccountCredit')

  // Error state for FortisElements
  const [fortisError, setFortisError] = useState(null)

  const { checkNum: checkNumError } = errors
  const checkNumHelperText = get(checkNumError, 'message')

  const { isCheck, isOther, isCreditCard, isACH, isCheckCashOther, isCreditCardOrACH } = getPaymentMethodByStatus(watchPaymentMethod)
  const hasZeroOrNegativeInvoices = checkHasZeroOrNegativeInvoices(unpaidInvoices)
  const isAccountCreditOptionDisabled = isLoading || !unpaidInvoices.length || !(initialCreditBalanceCents || hasZeroOrNegativeInvoices)

  const selectedAccountId = selectedOption?.accountId || selectedOption?.id
  const { isFortis } = usePaymentProvider(selectedAccountId)

  const totalInvoiceAmountCents = useMemo(() => {
    return sortedUnpaidInvoices.reduce((sum, invoice) => sum + (invoice.usedPartialAmount || invoice.invoiceAmount), 0)
  }, [sortedUnpaidInvoices])

  const handleResetCard = (paymentMethod = false) => {
    setValue('isNewCard', paymentMethod)
    setValue('selectedPaymentTokenDetails', {})
    setValue('cardHolderName', '')
    setValue('cvv', '')
    setValue('isSaveNewMethod', false)
    setValue('cardNickName', '')
    setValue('autoPayTime', null)
    // Clear Fortis error when resetting
    setFortisError(null)
  }

  const handleCardChange = (method, paymentMethod = watchPaymentMethod) => {
    const accountToken = get(method, 'accountToken', '')
    const cardHolderName = get(method, 'cardHolderName', '')
    const accountType = get(method, 'accountType')
    const expiry = get(method, 'expiry')
    const cardNickName = get(method, 'cardNickName', '')

    setValue('selectedPaymentTokenDetails', { accountToken, accountType: getIsCreditCard(paymentMethod) ? null : accountType, expiry })
    setValue('cardHolderName', cardHolderName)
    setValue('cvv', '')
    setValue('isNewCard', false)
    setValue('isSaveNewMethod', false)
    setValue('cardNickName', cardNickName)
    setValue('autoPayTime', null)
    // Clear Fortis error when changing cards
    setFortisError(null)
  }

  const safeFocus = fieldName => {
    if (!fieldName) return

    try {
      setTimeout(() => {
        // Check if field exists in the form before trying to focus
        const fieldExists = document.querySelector(`[name="${fieldName}"]`)
        if (fieldExists) {
          setFocus(fieldName)
        }
      }, 300)
    } catch (error) {
      console.warn('Could not set focus on field:', fieldName, error)
    }
  }

  const handlePaymentMethodChange = newPaymentMethod => {
    // Clear Fortis error when changing payment method
    setFortisError(null)

    const isAccountCredit = getIsAccountCredit(newPaymentMethod)
    if (isAccountCredit) {
      setValue('amount', 0)
      onAmountChange(0, true)
    }

    setValue('paymentMethod', newPaymentMethod)
    setValue('checkNum', '')

    let focusKey = ''

    if (getIsCreditCardOrACH(newPaymentMethod)) {
      const savedMethods = getSavedMethodsByPaymentType(savedPaymentMethods, newPaymentMethod)
      if (savedMethods.length > 0) {
        handleCardChange(savedMethods[0], newPaymentMethod)
        focusKey = 'amount'
      } else {
        handleResetCard(newPaymentMethod)
        focusKey = getIsCreditCard(newPaymentMethod) ? 'cardHolderName' : ''
      }
    } else {
      handleResetCard()
    }

    if ([CHECK, OTHER].includes(newPaymentMethod)) {
      focusKey = 'checkNum'
    }

    if (getIsCash(newPaymentMethod)) {
      focusKey = 'amount'
    }

    if (focusKey) {
      safeFocus(focusKey)
    }

    clearErrors()
  }

  const handleEnterPress = event => {
    if (event.key === 'Enter' || event.key === 'NumpadEnter') {
      onNextBtnClick()
    }
  }

  const handleNewCardTokenChange = useCallback(
    response => {
      setValue('selectedPaymentTokenDetails', { ...response })
    },
    [setValue]
  )

  const handleFortisPaymentFinished = useCallback(
    result => {
      // Respect Fortis outcome: proceed only when explicitly approved
      const token = get(result, 'data.id', '')
      const holderName = get(result, 'data.account_holder_name', '')
      const expiry = get(result, 'data.exp_date', '')

      if (!token) {
        setValue('selectedPaymentTokenDetails', {})
        if (typeof onFortisLoadingChange === 'function') {
          onFortisLoadingChange(false)
        }
        return
      }

      if (holderName) {
        setValue('cardHolderName', holderName)
        setValue('cardNickName', holderName)
      }

      if (token) {
        const paymentMethodType = get(result, 'data.payment_method')
        const isCC = paymentMethodType === 'cc'
        const fortisAccountType = get(result, 'data.account_type')
        const mappedAccountType = fortisAccountType === 'checking' ? 'Checking' : 'Saving'
        setValue('selectedPaymentTokenDetails', {
          accountToken: token,
          expiry,
          ...(!isCC && { accountType: mappedAccountType }),
        })
        if (typeof onFortisTokenized === 'function') {
          onFortisTokenized()
        }
      }
    },
    [onFortisTokenized, setValue]
  )

  const handleFortisError = useCallback(
    error => {
      setFortisError('Unable to load payment form. Please select a different payment method.')

      if (typeof onFortisLoadingChange === 'function') {
        onFortisLoadingChange(false)
      }
    },
    [onFortisLoadingChange]
  )

  const handleFortisChangeSaveMethod = useCallback(
    checked => {
      setValue('isSaveNewMethod', checked)
      if (!checked) {
        setValue('cardNickName', '')
        setValue('autoPayTime', null)
      }
    },
    [setValue]
  )

  const getValidationRules = () => ({
    validate: {
      notExceedAvailable: value => {
        const numericValue = parseFloat(value) || 0
        return numericValue <= formatCentsToDollars(initialCreditBalanceCents) || 'Cannot exceed available credit'
      },
      notExceedInvoiceAmount: value => {
        if (totalInvoiceAmountCents === 0) return true
        const numericValue = parseFloat(value) || 0
        return numericValue <= formatCentsToDollars(totalInvoiceAmountCents) || 'Cannot exceed invoice amount'
      },
      notNegative: value => {
        const numericValue = parseFloat(value) || 0
        return numericValue >= 0 || 'Amount must be positive'
      },
    },
  })

  const handleSwitchChange = checked => {
    if (checked) {
      setValue('accountCreditAmount', getValues('defaultAccountCreditAmount'))
      const amount = getValues('amount')
      const accountCreditAmount = getValues('accountCreditAmount')
      onAmountChange(amount, true, accountCreditAmount)
    } else {
      setValue('accountCreditAmount', 0)

      const currentPaymentAmount = watchAmount || 0
      const totalNeeded = formatCentsToDollars(totalInvoiceAmountCents)

      if (currentPaymentAmount < totalNeeded) {
        setValue('amount', 0)
        onAmountChange(0, true, 0)
      } else {
        setValue('amount', totalNeeded)
        onAmountChange(totalNeeded, true, 0)
      }
    }
  }

  useEffect(() => {
    if (initialCreditBalanceCents > 0) {
      setValue('applyAccountCredit', true)
      // Trigger the switch change handler to set initial values
      handleSwitchChange(true)
    }
  }, [])

  return (
    <>
      <Typography fontWeight={500} variant="h5">
        {capitalize(T.PAYMENT_TYPE)}
      </Typography>
      <Grid container columnSpacing={2} my={2}>
        <Grid size={6}>
          <HHFormDesktopDatePicker
            disableFuture
            control={control}
            disabled={!watchPaymentMethod}
            label={T.PAYMENT_DATE}
            name="paymentDate"
            shouldDisableDate={date => checkIsLocked(formatDateToBEFormatDateFns(date))}
            TextFieldProps={{ deprecatedLabel: false }}
          />
        </Grid>
      </Grid>
      <FormControl component="fieldset" disabled={!selectedOption || isLoading}>
        <RadioGroup row value={watchPaymentMethod} onChange={e => handlePaymentMethodChange(e.target.value)}>
          <Box width="100%">
            <FormControlLabel control={<Radio />} label={T.CHECK} value={CHECK} />
            <FormControlLabel control={<Radio />} label={T.CASH} value={CASH} />
            <FormControlLabel control={<Radio />} label={T.OTHER} value={OTHER} />
          </Box>
          <Box width="100%">
            <FormControlLabel control={<Radio />} label={capitalize(T.CREDIT_CARD)} value={CREDIT_CARD} />
            <FormControlLabel control={<Radio />} label={T.ACH} value={ACH} />
            <FormControlLabel
              control={<Radio disabled={isAccountCreditOptionDisabled} />}
              label={capitalize(T.ACCOUNT_CREDIT)}
              value={ACCOUNT_CREDIT}
            />
          </Box>
        </RadioGroup>
      </FormControl>
      {isCheckCashOther && (
        <Grid container columnSpacing={2} mt={2}>
          {(isCheck || isOther) && (
            <Grid size="grow">
              <HHFormTextField
                fullWidth
                control={control}
                deprecatedLabel={false}
                error={checkNumError}
                helperText={checkNumHelperText}
                label={isCheck ? `${T.CHECK} #` : T.EXTERNAL_PAYMENT_NUMBER}
                name="checkNum"
                placeholder={isCheck ? `${T.CHECK} #` : T.EXTERNAL_PAYMENT_NUMBER}
                required={isCheck}
                rules={{ required: isCheck ? T.CANNOT_BE_EMPTY : false }}
              />
            </Grid>
          )}
          <Grid size="grow">
            <AmountField onChange={onAmountChange} onKeyDown={handleEnterPress} />
          </Grid>
        </Grid>
      )}
      {isCreditCardOrACH && (
        <Box mt={1}>
          <CreditCardAchListing
            paymentMethods={savedPaymentMethods}
            selectedPaymentTokenDetails={watchSelectedPaymentTokenDetails}
            type={watchPaymentMethod}
            onPaymentMethodSelect={handleCardChange}
          />
          <FormControl component="fieldset">
            <RadioGroup value={watchIsNewCard} onChange={event => handleResetCard(event.target.value)}>
              <FormControlLabel
                control={<Radio />}
                label={watchPaymentMethod === ACH ? T.NEW_ACH : T.NEW_CARD}
                value={watchPaymentMethod}
              />
            </RadioGroup>
          </FormControl>
          {watchIsNewCard && document.getElementById('takePaymentGroup') && (
            <Box maxHeight={250} overflow="auto">
              {fortisError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFortisError(null)}>
                  {fortisError}
                </Alert>
              )}
              {isFortis && (
                <FortisElements
                  hideSubmitButton
                  showSaveMethodCheckbox
                  accountId={accountId}
                  action="tokenization"
                  billingAddress={billingAddress}
                  hasSurcharge={hasSurcharge}
                  methodType={isCreditCard ? 'cc' : 'ach'}
                  saveMethod={watchIsSaveNewMethod}
                  submitRef={fortisSubmitRef}
                  onChangeSaveMethod={handleFortisChangeSaveMethod}
                  onError={handleFortisError}
                  onPaymentFinished={handleFortisPaymentFinished}
                  onSubmitLoadingChange={onFortisLoadingChange}
                />
              )}
              {!isFortis && (
                <>
                  {isCreditCard && <RenderCreditCard accountId={selectedAccountId} onNewCardTokenChange={handleNewCardTokenChange} />}
                  {isACH && <RenderACH accountId={selectedAccountId} onNewCardTokenChange={handleNewCardTokenChange} />}
                </>
              )}
              {!isFortis && (
                <FormControlLabel
                  control={<HHFormCheckboxField control={control} name="isSaveNewMethod" onChange={() => setValue('cardNickName', '')} />}
                  label={T.SAVE_METHOD}
                  sx={{ mt: 2 }}
                />
              )}
              {watchIsSaveNewMethod && !isFortis && (
                <HHFormTextField
                  fullWidth
                  control={control}
                  deprecatedLabel={false}
                  label={T.METHOD_NICKNAME}
                  name="cardNickName"
                  placeholder={T.METHOD_NICKNAME}
                  sx={{ mt: 1 }}
                />
              )}
              {watchIsSaveNewMethod && (
                <Box mt={2} pb={1}>
                  <CommonSwitch
                    isChecked={Boolean(watchAutoPayTime)}
                    title={T.AUTOPAY}
                    onChange={checked => setValue('autoPayTime', checked ? DEFAULT_AUTOPAY_TIMING : null)}
                  />
                  {watchAutoPayTime && <AutopayDisclaimer />}
                </Box>
              )}
            </Box>
          )}

          <Box mt={2}>
            <AmountField onChange={onAmountChange} onKeyDown={handleEnterPress} />
            {/* Rate estimates come from the Fortis calculate-surcharge API — Fortis-only. */}
            {isCreditCard && hasSurcharge && isFortis && (
              <Box mt={2}>
                <SurchargePanel
                  accountId={selectedAccountId}
                  amount={watchAmount}
                  token={get(watchSelectedPaymentTokenDetails, 'accountToken', '')}
                  zip={get(billingAddress, 'zipCode', '')}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
      {initialCreditBalanceCents > 0 && (
        <Box mt={2}>
          <Box alignItems="center" display="flex" justifyContent="space-between" mb={2}>
            <HHFormSwitchField
              control={control}
              disabled={initialCreditBalanceCents === 0}
              label="Apply account credit"
              name="applyAccountCredit"
              onChange={handleSwitchChange}
            />
          </Box>

          {applyAccountCredit && (
            <HHFormPriceField
              fullWidth
              control={control}
              deprecatedLabel={false}
              disabled={!applyAccountCredit}
              error={!!errors.accountCreditAmount}
              helperText={errors.accountCreditAmount?.message}
              label="Credit amount"
              name="accountCreditAmount"
              rules={getValidationRules()}
              onChange={onAccountCreditAmountChange}
            />
          )}
        </Box>
      )}
      {watchPaymentMethod && (
        <>
          <Divider sx={{ my: 2 }} />
          <HHFormTextField fullWidth control={control} deprecatedLabel={false} label={T.NOTE} name="note" placeholder="Leave a note" />
        </>
      )}
      {isSinglePayment && watchPaymentMethod && (
        <Box mt={2}>
          <Typography fontWeight={500} variant="h5">
            Payment group
          </Typography>

          <Box mt={2}>
            <AddToGroupField groupId={groupId} onChange={event => onChange('groupId', event.target.value)} />
          </Box>
        </Box>
      )}
      {watchPaymentMethod && (
        <>
          <Box display="flex" justifyContent="space-between" mt={3}>
            <Button color="warning" fullWidth={!isSinglePayment} variant="outlined" onClick={onClearClick}>
              Clear fields
            </Button>
          </Box>
        </>
      )}
    </>
  )
}

PaymentType.propTypes = {
  isLoading: PropTypes.bool,
  savedPaymentMethods: PropTypes.array,
  params: PropTypes.object,
  hasSurcharge: PropTypes.bool,
  unpaidInvoices: PropTypes.array,
  initialCreditBalanceCents: PropTypes.number,
  isSinglePayment: PropTypes.bool,
  invoicesToPay: PropTypes.array,
  sortedUnpaidInvoices: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onAmountChange: PropTypes.func.isRequired,
  onNextBtnClick: PropTypes.func.isRequired,
  onClearClick: PropTypes.func.isRequired,
  onAccountCreditAmountChange: PropTypes.func.isRequired,
  accountId: PropTypes.string,
  fortisSubmitRef: PropTypes.object,
  onFortisTokenized: PropTypes.func,
  onFortisLoadingChange: PropTypes.func,
  billingAddress: PropTypes.object,
}

export default PaymentType
